import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { Sparkles, Zap, Megaphone, ShieldCheck, Loader2, ArrowRight, TicketPercent, CheckCircle2, XCircle } from "lucide-react";

const ICONS = { premium_job_post: Sparkles, boost_job_7d: Zap, featured_contractor_30d: ShieldCheck, blast_credits_10: Megaphone };
const HUES = {
  premium_job_post: "from-orange-50 to-amber-50 border-orange-300",
  boost_job_7d: "from-fuchsia-50 to-pink-50 border-fuchsia-300",
  featured_contractor_30d: "from-emerald-50 to-teal-50 border-emerald-300",
  blast_credits_10: "from-blue-50 to-sky-50 border-blue-300",
};

export default function Plans() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);   // {code, percent_off, message} | null
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data));
    if (user?.role === "contractor" || user?.role === "hr" || user?.role === "admin") {
      api.get("/jobs/mine").then((r) => setMyJobs(r.data.filter((j) => j.status !== "closed"))).catch(() => {});
    }
  }, [user]);

  const validateCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setDiscount(null); return; }
    setValidating(true);
    try {
      const { data } = await api.post("/payments/discount/validate", { code: c });
      setDiscount({ code: c, percent_off: data.percent_off, message: data.message });
      toast.success(`Code ${c} applied — ${data.percent_off}% off!`);
    } catch (e) {
      setDiscount(null);
      toast.error(formatError(e));
    } finally { setValidating(false); }
  };

  const buy = async (pkg) => {
    if (pkg.requires_job) {
      const openJobs = myJobs;
      if (!openJobs.length) {
        toast.error("Post an open job first, then come back to upgrade it.");
        nav("/post-job"); return;
      }
      const list = openJobs.map((j, i) => `${i + 1}. ${j.title} (${j.location})`).join("\n");
      const pick = window.prompt(`Which job? Enter number:\n\n${list}`);
      const idx = Number(pick) - 1;
      const job = openJobs[idx];
      if (!job) return;
      await startCheckout(pkg.id, job.id);
    } else { await startCheckout(pkg.id, null); }
  };

  const startCheckout = async (packageId, jobId) => {
    setBusy(packageId);
    const prefill = { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" };
    const result = await openRazorpayCheckout({ packageId, jobId, prefill, discountCode: discount?.code });
    setBusy("");
    if (result.ok && result.tx?.job_id) nav(`/jobs/${result.tx.job_id}`);
    else if (result.ok) nav("/billing");
  };

  const perkList = useMemo(() => ({
    premium_job_post: ["Company logo on job card", "Extended description", "Priority in search"],
    boost_job_7d: ["Pinned to top of listings", "For a full 7 days", "Instantly visible to all workers"],
    featured_contractor_30d: ["Verified checkmark on profile", "Unlimited premium posts", "Priority in search"],
    blast_credits_10: ["10 urgent-blast credits", "Bypass the 5/hr rate limit", "Ideal for shutdown hires"],
  }), []);

  const finalAmount = (base) => discount ? +(base * (1 - discount.percent_off / 100)).toFixed(2) : base;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="plans-page">
      <div className="text-center">
        <h1 className="font-display text-4xl font-extrabold text-sarthi-text sm:text-5xl">Premium Plans</h1>
        <p className="mt-3 text-lg font-medium text-sarthi-muted">Pay only for the amplification you actually need. No subscriptions, no lock-in.</p>
      </div>

      {/* Discount code entry */}
      <div className="mx-auto mt-6 max-w-md sarthi-card p-4" data-testid="discount-code-panel">
        <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-sarthi-muted">
          <TicketPercent className="h-4 w-4" /> Have a promo code?
        </div>
        <div className="flex gap-2">
          <input
            data-testid="discount-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && validateCode()}
            placeholder="e.g. SAVE80"
            className="w-full rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-base font-bold uppercase tracking-wider focus:border-sarthi-primary focus:outline-none"
          />
          <button data-testid="discount-code-apply" onClick={validateCode} disabled={validating} className="sarthi-btn-primary px-4 py-2 text-sm text-white disabled:opacity-50">
            {validating ? "…" : "Apply"}
          </button>
        </div>
        {discount && (
          <div data-testid="discount-applied" className="mt-2 flex items-center gap-2 text-sm font-bold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> {discount.percent_off}% OFF applied ({discount.code}) — {discount.message}
            <button onClick={() => { setDiscount(null); setCode(""); }} className="ml-auto text-sarthi-muted hover:text-red-600"><XCircle className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {packages.map((pkg) => {
          const Icon = ICONS[pkg.id] || Sparkles;
          const hue = HUES[pkg.id] || "from-stone-50 to-stone-100 border-sarthi-border";
          const isBusy = busy === pkg.id;
          const final = finalAmount(pkg.amount);
          return (
            <div key={pkg.id} data-testid={`plan-card-${pkg.id}`} className={`sarthi-card relative flex flex-col overflow-hidden bg-gradient-to-br p-6 ${hue}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sarthi-primary text-white shadow-md">
                  <Icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-display text-lg font-extrabold leading-tight text-sarthi-text">{pkg.label}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">{pkg.requires_job ? "per job" : "one-time"}</div>
                </div>
              </div>

              <div className="mt-5">
                {discount ? (
                  <>
                    <span className="font-display text-4xl font-extrabold text-sarthi-text" data-testid={`price-final-${pkg.id}`}>₹{final}</span>
                    <span className="ml-2 text-lg font-semibold text-sarthi-muted line-through">₹{pkg.amount}</span>
                    <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">-{discount.percent_off}%</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl font-extrabold text-sarthi-text" data-testid={`price-base-${pkg.id}`}>₹{pkg.amount}</span>
                    <span className="ml-1 text-sm font-semibold text-sarthi-muted">{pkg.currency?.toUpperCase()}</span>
                  </>
                )}
              </div>

              <ul className="mt-4 flex-1 space-y-2 text-sm font-semibold text-sarthi-text">
                {(perkList[pkg.id] || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sarthi-primary" />{p}</li>
                ))}
              </ul>

              <button data-testid={`buy-${pkg.id}`} onClick={() => buy(pkg)} disabled={isBusy} className="sarthi-btn-primary mt-6 inline-flex items-center justify-center gap-2 py-3 text-white disabled:opacity-50">
                {isBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <>Pay ₹{final} <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center text-sm font-medium text-sarthi-muted">
        Payments processed securely by Razorpay (UPI · Cards · Netbanking). See your history at{" "}
        <a href="/billing" className="font-bold text-sarthi-primary underline" data-testid="billing-history-link">Billing</a>.
      </div>
    </div>
  );
}
